import _                                                                                                                                from 'underscore';
import moment, { Moment }                                                                                                               from 'moment';
import { TimeService }                                                                                                                  from './TimeService';
import { CorvalBusinessFunction, CorvalProject, CorvalProjectActivity, CorvalProjectCustomTimingMilestone, CorvalProjectVersion, Uuid } from '../types';
import { fieldQuestions }                                                                                                               from '../views/pages/projects/survey/SurveyQuestionProjectDataFields';
import { fragmentFieldsToArray }                                                                                                        from '../util';
import { SystemAccountingQueries }                                                                                                      from '../queries';
import { ITermDictionary }                                                                                                              from '../lang/LangTypes';
import { LangService }                                                                                                                  from './LangService';

import { ACTIVITY_CRITICALITY_INFO, ACTIVITY_STATUS_INFO, ActivityCriticalityType, ActivityStatusType, CATEGORY_INFO, CategoryFieldType, CategoryInfoItem, EnumDescriptorType, OBJECTIVE_STATUS_INFO, ObjectiveStatusType, PROJECT_MILESTONE_INFO, PROJECT_MILESTONES, ProjectCustomMilestonesField, ProjectCustomStartDateField, ProjectMilestoneField, ProjectMilestoneInfoItem, ProjectMilestoneType, ProjectMilestoneTypes, projectTimelineFields } from '@src/constants';


export type ProjectDateFieldObject = {
    fieldName: ProjectMilestoneField | ProjectCustomStartDateField,
    dependency: ProjectMilestoneField | ProjectCustomStartDateField,
    label: string,
    description: string,
}

export type ProjectTimelineDateFieldObject = {
    id: Uuid,
    fieldId: Uuid | ProjectMilestoneField | ProjectCustomStartDateField,
    label: string,
    description: string,
    value: string,
    rawValue: string,
    order: number,
    type: ProjectMilestoneTypes,
}

export class ProjectService {

    // Gets a project version object by default criteria, usually the initial or earliest-editable project version.
    static getDefaultProjectVersion = ( projectVersions: Partial<CorvalProjectVersion>[] ): Partial<CorvalProjectVersion> => {

        if( _.isEmpty( projectVersions ) ) return null;

        return _.find( projectVersions, ( version ) => { return version?.isDefaultVersion === true; } ) ?? null;
    };

    // Gets a project version object by id, or returns the default version if not found; Requires a project version array to be present.
    static getProjectVersionById = ( projectVersions: Partial<CorvalProjectVersion>[], projectVersionId: Uuid, fallbackToDefaultVersion = true ): Partial<CorvalProjectVersion> => {

        const version          = _.findWhere( projectVersions, { id: projectVersionId } ),
              fallbackResponse = fallbackToDefaultVersion ? ProjectService.getDefaultProjectVersion( projectVersions ) : null;

        return version ?? fallbackResponse;

    };

    static getDatefieldsAsOrderedArray = ( terminologyMethod: ( term: string ) => string = null ): ProjectDateFieldObject[] => {

        let datefields = _.sortBy( PROJECT_MILESTONE_INFO, m => m.ORDER );

        return datefields.map( milestone => ( {
                fieldName:   milestone.PROJECT_FIELD,
                dependency:  milestone.DEPENDENT_FIELD,
                label:       terminologyMethod ? terminologyMethod( milestone.LABEL ) : milestone.LABEL,
                description: terminologyMethod ? terminologyMethod( milestone.DESCRIPTION ) : milestone.DESCRIPTION,
            } )
        );
    };

    static getMinMaxActivityDates = ( projectActivities: CorvalProjectActivity[] ) => {

        let dates: Moment[] = [];

        projectActivities.map( a => {

            if( a.startTimeUtc && a.endTimeUtc ){

                dates.push( TimeService.ISOtoMoment( a.startTimeUtc ) );
                dates.push( TimeService.ISOtoMoment( a.endTimeUtc ) );
            }
        } );

        let earliestDate = moment.min( dates ),
            latestDate   = moment.max( dates );

        return { earliestDate, latestDate };
    };

    static getMapTimeframeFromStart = ( startDate: Moment, maxQuarters = 4 ): [ mapStartDate: Moment, mapEndDate: Moment ] => {

        // update default
        let mapStartDate = startDate.clone().startOf( 'quarter' );
        let mapEndDate = mapStartDate.clone().add( maxQuarters - 1, 'quarters' ).endOf( 'quarter' );

        return [
            mapStartDate,
            mapEndDate
        ];
    };

    static getMilestoneInfoFromField = ( milestoneField: ProjectMilestoneField ): ( ProjectMilestoneInfoItem | null ) => {

        return _.chain( PROJECT_MILESTONE_INFO )
                .values()
                .findWhere( { PROJECT_FIELD: milestoneField } )
                .value()
               ?? null;

    };

    static getMilestoneInfoFromInfo = ( attribute: string, value: any ): ( ProjectMilestoneInfoItem | null ) => {

        return _.chain( PROJECT_MILESTONE_INFO )
                .values()
                .findWhere( { [ attribute ]: value } )
                .value()
               ?? null;
    };

    static getMilestoneTypeFromField = ( milestoneField: ProjectMilestoneField ): ( ProjectMilestoneType | null ) => {

        return _.findKey( PROJECT_MILESTONE_INFO, milestone => milestone.PROJECT_FIELD === milestoneField ) ?? null;
    };

    static getMilestoneInfoFromType = ( milestoneType: ProjectMilestoneType ): ( ProjectMilestoneInfoItem | null ) => {

        return PROJECT_MILESTONE_INFO[ milestoneType ] ?? null;
    };

    static getLastMilestoneInfo = ( attribute?: keyof ProjectMilestoneInfoItem ) => {

        let lastOrderedMilestone = _.max( _.values( PROJECT_MILESTONE_INFO ), milestoneInfo => milestoneInfo.ORDER );

        if( lastOrderedMilestone ){
            return attribute ? lastOrderedMilestone[ attribute ] : lastOrderedMilestone;
        }

        return null;
    };

    static getFirstMilestoneInfo = ( attribute?: keyof ProjectMilestoneInfoItem ) => {

        let firstOrderedMilestone = _.min( _.values( PROJECT_MILESTONE_INFO ), milestoneInfo => milestoneInfo.ORDER );

        if( firstOrderedMilestone ){
            return attribute ? firstOrderedMilestone[ attribute ] : firstOrderedMilestone;
        }

        return null;
    };

    static isMilestoneTypeLowerBoundary = ( milestoneType: ProjectMilestoneType ): boolean => {

        return ProjectService.isMilestoneTypeBoundary( milestoneType ).isLowerMilestoneBoundary;
    };

    static isMilestoneTypeUpperBoundary = ( milestoneType: ProjectMilestoneType ): boolean => {

        return ProjectService.isMilestoneTypeBoundary( milestoneType ).isUpperMilestoneBoundary;
    };

    static getLowerBoundaryMilestone = ( asType = false ): ProjectMilestoneField | ProjectMilestoneType => {

        let boundaryField = ProjectService.getBoundaryMilestoneField( false ) ?? null,
            boundaryType  = boundaryField ? ProjectService.getMilestoneTypeFromField( boundaryField ) : null;

        return asType
               ? boundaryType as ProjectMilestoneType
               : boundaryField as ProjectMilestoneField;
    };

    static getUpperBoundaryMilestone = ( asType = false ): ProjectMilestoneField | ProjectMilestoneType => {

        let boundaryField = ProjectService.getBoundaryMilestoneField( true ) ?? null,
            boundaryType  = boundaryField ? ProjectService.getMilestoneTypeFromField( boundaryField ) : null;

        return asType
               ? boundaryType as ProjectMilestoneType
               : boundaryField as ProjectMilestoneField;
    };

    static getBoundaryMilestoneField = ( upperBoundary = false ): ProjectMilestoneField => {

        let milestones = _.values( PROJECT_MILESTONE_INFO );

        return _.findWhere( milestones, { ORDER: upperBoundary ? ( milestones.length - 1 ) : 0 } )?.PROJECT_FIELD as ProjectMilestoneField ?? null;
    };

    static isMilestoneTypeBoundary = ( milestoneType: ProjectMilestoneType ): Record<string, boolean> => {

        // reset offset if boundary isn't applicable
        let milestones               = _.values( PROJECT_MILESTONE_INFO ),
            milestoneTypeAsField     = ProjectService.getMilestoneInfoFromType( milestoneType ).PROJECT_FIELD as ProjectMilestoneField,
            isLowerMilestoneBoundary = ( milestoneTypeAsField === ( _.findWhere( milestones, { ORDER: 0 } )?.PROJECT_FIELD as ProjectMilestoneField ) ?? null ),
            isUpperMilestoneBoundary = ( milestoneTypeAsField === ( _.findWhere( milestones, { ORDER: milestones.length - 1 } )?.PROJECT_FIELD as ProjectMilestoneField ) ?? null );

        return {
            isLowerMilestoneBoundary,
            isUpperMilestoneBoundary,
        };
    };

    static getsMilestoneSiblings = ( milestoneType: ProjectMilestoneType ): Record<string, ProjectMilestoneField> => {

        // reset offset if boundary isn't applicable
        let milestones            = _.values( PROJECT_MILESTONE_INFO ),
            milestoneTypeIndex    = ProjectService.getMilestoneInfoFromType( milestoneType ).ORDER,
            lowerMilestoneSibling = ( _.findWhere( milestones, { ORDER: ( milestoneTypeIndex - 1 ) } )?.PROJECT_FIELD as ProjectMilestoneField ) ?? null,
            upperMilestoneSibling = ( _.findWhere( milestones, { ORDER: ( milestoneTypeIndex + 1 ) } )?.PROJECT_FIELD as ProjectMilestoneField ) ?? null;

        return {
            lowerMilestoneSibling,
            upperMilestoneSibling,
        };
    };

    static hasCustomStartDate = ( projectLikeObject: Pick<CorvalProject, ProjectCustomStartDateField> = {} ) => {

        return ( projectLikeObject?.customStartDateUtc ?? null ) ? !_.isEmpty( projectLikeObject.customStartDateUtc ) : false;

    };

    static hasCustomMilestones = ( projectLikeObject: Pick<CorvalProject, ProjectCustomMilestonesField> = {} ) => {

        return ( projectLikeObject?.customTimingMilestones ?? null ) ? !_.isEmpty( projectLikeObject.customTimingMilestones ) : false;

    };

    static sortCustomMilestonesByDate = ( customTimingMilestones: CorvalProjectCustomTimingMilestone[] = [] ) => {

        return _.sortBy( customTimingMilestones, item =>
            moment.isMoment( item.date )
            ? TimeService.asYearFirstDisplayDate( item.date )
            : TimeService.ISOtoMoment( TimeService.asYearFirstDisplayDate( item.date ) )
        );
    };

    static getDefaultTimelineDataObjects = ( placeHolderDate = null ): ProjectTimelineDateFieldObject[] => {

        const defaultPlaceholderProject    = _.object( [ ...projectTimelineFields ], Array( projectTimelineFields.length ).fill( placeHolderDate ) ),
              defaultProjectTimelineFields = ProjectService.getTimelineDataObjectFromProjectFields( {
                  ...defaultPlaceholderProject,
                  customStartDateUtc:     null,
                  customTimingMilestones: [],
              } as Pick<CorvalProject, ProjectMilestoneField | "customStartDateUtc" | "customTimingMilestones"> );

        return defaultProjectTimelineFields;
    };

    static getTimelineDataObjectFromProjectFields = (
        project: Pick<CorvalProject, ProjectMilestoneField | ProjectCustomStartDateField | ProjectCustomMilestonesField>,
        termDictionary: ITermDictionary = null,
    ): ProjectTimelineDateFieldObject[] => {

        type OrderedFieldSet<D = any> = [ field: ProjectMilestoneField | ProjectCustomStartDateField, value: D ];

        /* Defined Project timeline Fields */
        let timelineFields = [ 'customStartDateUtc', ...fragmentFieldsToArray( SystemAccountingQueries.fragments.PROJECTS.TIMELINE_FIELDS() ) ];

        /* Custom flags */
        let hasCustomStartDate  = ProjectService.hasCustomStartDate( project ),
            hasCustomMilestones = ProjectService.hasCustomMilestones( project );

        // Project fields ordered by time
        let orderedProjectDateFields: Array<ProjectMilestoneField | ProjectCustomStartDateField> =
                _.chain( project )
                 .pick( timelineFields )
                 .pairs()
                 .filter( ( [ field ]: OrderedFieldSet<string> ) => ( ( !hasCustomStartDate ) ? ( field !== 'customStartDateUtc' ) : true ) )
                 .map( ( [ field, fieldValue ]: OrderedFieldSet<string> ) => [ field, TimeService.ISOtoMoment( fieldValue ) ] )
                 .value()
                 .sort( ( [ , dateObjA ]: OrderedFieldSet<Moment>, [ , dateObjB ]: OrderedFieldSet<Moment> ) => ( dateObjA.valueOf() - dateObjB.valueOf() ) )
                 .map( ( [ field ]: OrderedFieldSet<Moment> ) => field );

        /* Project timeline field objects */
        let orderedFields: ProjectTimelineDateFieldObject[] = orderedProjectDateFields.map( ( field, index ) => {

            return {
                order:       index,
                fieldId:     field,
                value:       TimeService.ISOtoDisplayDate( project[ field ] ),
                rawValue:    project[ field ],
                label:       LangService.getTermFromDictionary( fieldQuestions[ field ].questionText, LangService.getDictionary( termDictionary ) ),
                description: LangService.getTermFromDictionary( fieldQuestions[ field ].description, LangService.getDictionary( termDictionary ) ),
                type:        field !== 'customStartDateUtc'
                             ? ProjectMilestoneTypes.PROJECT_MILESTONE
                             : ProjectMilestoneTypes.PROJECT_START_DATE
            };
        } );

        /* Custom timeline field objects */
        if( hasCustomMilestones ){

            project.customTimingMilestones.map( ( customMilestone, index ) => {

                orderedFields.push( {
                    order:       index,
                    fieldId:     customMilestone.id,
                    value:       TimeService.ISOtoDisplayDate( customMilestone.date ),
                    rawValue:    customMilestone.date,
                    label:       customMilestone.name,
                    description: customMilestone.description,
                    type:        ProjectMilestoneTypes.CUSTOM_USER_MILESTONE,
                } as ProjectTimelineDateFieldObject );
            } );
        }

        // sort, set order
        orderedFields = _.chain( orderedFields )
                         .sortBy( item => TimeService.asYearFirstDisplayDate( TimeService.ISOtoMoment( item.rawValue ) ) )
                         .map( ( field, index ) => ( { ...field, order: index } ) )
                         .value();

        return orderedFields;
    };

    static getTimelineDataObjectFromSet = ( fieldId: string, fieldObjects: ProjectTimelineDateFieldObject[] ): ProjectTimelineDateFieldObject => {

        let timelineField = fieldId;

        // Check for a timefield value that is a milestone type
        if( _.keys( PROJECT_MILESTONES ).includes( fieldId ) ){

            const valueAsTimelineFieldType = ProjectService.getMilestoneInfoFromType( fieldId as ProjectMilestoneType );

            if( valueAsTimelineFieldType ){

                // convert the milestone type to a project field
                timelineField = valueAsTimelineFieldType.PROJECT_FIELD;
            }
        }

        return _.findWhere( fieldObjects, { fieldId: timelineField } ) ?? null;
    };

    static timelineDataObjectIsSystemMilestone = ( item: ProjectTimelineDateFieldObject | { type: ProjectMilestoneTypes } ) => {

        return ( item.type === ProjectMilestoneTypes.PROJECT_MILESTONE );
    };
    static timelineDataObjectIsCustomStartDate = ( item: ProjectTimelineDateFieldObject | { type: ProjectMilestoneTypes } ) => {

        return ( item.type === ProjectMilestoneTypes.PROJECT_START_DATE );
    };
    static timelineDataObjectIsCustomMilestone = ( item: ProjectTimelineDateFieldObject | { type: ProjectMilestoneTypes } ) => {

        return ( item.type === ProjectMilestoneTypes.CUSTOM_USER_MILESTONE );
    };

    static getProjectActivityRecurrenceMilestones = ( projectActivity: CorvalProjectActivity, flatten = false ) => {

        let projectMilestones = _.pluck( projectActivity.recurrenceMilestones ?? [], 'recurrenceMilestone' ),
            customMilestones  = _.pluck( projectActivity.recurrenceCustomMilestones ?? [], 'projectCustomTimingMilestoneId' );

        return ( flatten )
               ? _.union( projectMilestones, customMilestones ) as Array<ProjectMilestoneType | Uuid>
               : [ projectMilestones, customMilestones ] as [ projectMilestones: ProjectMilestoneType[], customMilestones: Uuid[] ];
    };

    static sortProjectActivityRecurrenceMilestoneSet = ( mixedMilestoneIdSet: Array<ProjectMilestoneField | ProjectMilestoneType | Uuid> ):
        [ projectMilestones: ProjectMilestoneField | ProjectMilestoneType[], customMilestones: Uuid[] ] => {

        // sort milestone set between project field names / enum types and custom milestone ids
        let [ systemMilestones, customMilestones ] = _.partition( mixedMilestoneIdSet, milestoneIdentifier => !ProjectService.isCustomMilestoneDefinitionId( milestoneIdentifier ) );

        return [
            systemMilestones,
            customMilestones,
        ];
    };

    // Used to test a value for an id or a named project field for milestones
    static isCustomMilestoneDefinitionId = ( milestoneIdentifier: ProjectMilestoneField | ProjectMilestoneType | Uuid ) => {

        return !(
            projectTimelineFields.includes( milestoneIdentifier ) ||        // project fields
            _.keys( PROJECT_MILESTONES ).includes( milestoneIdentifier )    // project field enums
        );
    };

    static getCategoryInfoFromType = ( categoryType: CategoryFieldType, attribute: keyof CategoryInfoItem = null ): ( CategoryInfoItem | CategoryInfoItem[ keyof CategoryInfoItem ] | null ) => {

        let info = CATEGORY_INFO[ categoryType ] ?? null;

        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static getBusinessFunctionsFromProjects = ( projects: Array<CorvalProject> ) => {

        const allBusinessFunctions = _.chain( projects )
                                      .map( project => project.businessFunctions ).flatten()
                                      .uniq( ( businessFunction: CorvalBusinessFunction ) => businessFunction.id )
                                      .value();

        return allBusinessFunctions;
    };

    static getActivityCriticalityInfoFromType = ( criticalityType: ActivityCriticalityType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {
        let info = ACTIVITY_CRITICALITY_INFO[ criticalityType ] ?? null;
        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static getObjectiveStatusInfoFromType = ( statusType: ObjectiveStatusType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {

        let info = OBJECTIVE_STATUS_INFO[ statusType ] ?? null;

        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static getActivityStatusInfoFromType = ( statusType: ActivityStatusType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {

        let info = ACTIVITY_STATUS_INFO[ statusType ] ?? null;

        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static isMedicalAffairs = ( businessFunctionType: CorvalBusinessFunction ): boolean => {

        return businessFunctionType.resourceCode === 'MEDICALAFFAIRS';
    };

    static containsMedicalAffairBusinessFunction = ( businessFunctionTypes: CorvalBusinessFunction[] ): boolean => {

        return _.contains( _.pluck( businessFunctionTypes, 'resourceCode' ) ?? [], 'MEDICALAFFAIRS' );
    };
}
